import React, { useState, useEffect, useCallback } from 'react';
import Layout from '@/components/Layout';
import PollenDotsNav from '@/components/PollenDotsNav';
import { API, useAuth } from '@/App';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Upload, Loader2, Trash2, IndianRupee, TrendingUp, TrendingDown, FileSpreadsheet
} from 'lucide-react';
import ConfirmDeleteDialog from '@/components/ConfirmDeleteDialog';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';

const PollenDotsFinancials = () => {
  const POLLEN_API = `${API}/pollen`;
  const { user } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const authFetch = (url, opts = {}) => {
    const h = {}; const t = localStorage.getItem('session_token'); if (t) h['Authorization'] = `Bearer ${t}`;
    return fetch(url, { ...opts, credentials: 'include', headers: { ...opts.headers, ...h } });
  };

  const fetchRecords = useCallback(async () => {
    try {
      const res = await authFetch(`${POLLEN_API}/financials/records`);
      if (res.ok) setRecords(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await authFetch(`${POLLEN_API}/financials/import-pnl`, { method: 'POST', body: fd });
      if (res.ok) {
        const result = await res.json();
        if (result.error) { toast.error(result.error); }
        else { toast.success('P&L data extracted successfully'); fetchRecords(); }
      } else { const err = await res.json(); toast.error(err.detail || 'Import failed'); }
    } catch (e) { toast.error('Upload error'); }
    setUploading(false);
    e.target.value = '';
  };

  const handleDelete = async (id) => {
    await authFetch(`${POLLEN_API}/financials/${id}`, { method: 'DELETE' });
    toast.success('Record deleted');
    setDeleteId(null);
    fetchRecords();
  };

  // Aggregate yearly data for chart — keep record ID for deletion
  const yearlyData = records.map(r => {
    const yt = r.yearly_totals || {};
    return {
      id: r.id,
      year: yt.year || r.year || r.period || 'Unknown',
      filename: r.filename,
      revenue: yt.total_revenue || 0,
      cogs: yt.total_cogs || 0,
      gross_profit: yt.gross_profit || 0,
      operating_expenses: yt.operating_expenses || 0,
      net_profit: yt.net_profit || 0,
      created_at: r.created_at,
    };
  }).sort((a, b) => a.year.localeCompare(b.year));

  // Aggregate monthly data — keep record ID for deletion
  const allMonthly = records.flatMap(r => (r.monthly_data || []).map(m => ({ ...m, record_id: r.id, source: r.filename }))).sort((a, b) => (a.month || '').localeCompare(b.month || ''));

  const fmt = (v) => v ? `${(v / 100000).toFixed(1)}L` : '0';
  const fmtFull = (v) => (v || 0).toLocaleString('en-IN');

  if (!['Pollen Dots User','Super Admin','Admin','Operator','Intern'].includes(user?.role)) return <Navigate to="/dashboard" replace />;
  // P&L restricted to profit-viewing roles
  if (!['Pollen Dots User','Super Admin','Admin'].includes(user?.role)) return <Navigate to="/pollen-dots-dashboard" replace />;
  if (loading) return <Layout><div className="flex items-center justify-center h-[60vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></Layout>;

  return (
    <Layout>
      <div className="space-y-4" data-testid="financials-page">
        <PollenDotsNav />

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">P&L Reports</h1>
            <p className="text-sm text-muted-foreground">Upload Zoho P&L statements to track historical revenue, costs & profit</p>
          </div>
          <label className="cursor-pointer">
            <input type="file" className="hidden" accept=".pdf,.xlsx,.xls,.csv,.txt,.docx" onChange={handleUpload} data-testid="pnl-upload" />
            <Button variant="default" asChild disabled={uploading}>
              <span>{uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}{uploading ? 'Extracting...' : 'Upload P&L Statement'}</span>
            </Button>
          </label>
        </div>

        {/* Yearly Summary Cards */}
        {yearlyData.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="yearly-summary">
            {yearlyData.map((y, i) => (
              <Card key={i} className="border-l-4 border-l-primary relative group">
                <Button variant="ghost" size="sm"
                  className="absolute top-2 right-2 h-7 w-7 p-0 text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-destructive transition-opacity"
                  onClick={() => setDeleteId(y.id)} data-testid={`delete-yearly-${y.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
                <CardContent className="pt-4 pb-3 px-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-muted-foreground">{y.year}</p>
                    <p className="text-[10px] text-muted-foreground truncate max-w-[80px]">{y.filename}</p>
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-sm"><span className="text-muted-foreground">Revenue</span><span className="font-bold">{fmtFull(y.revenue)}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-muted-foreground">COGS</span><span className="font-medium text-red-600">{fmtFull(y.cogs)}</span></div>
                    <div className="flex justify-between text-sm border-t pt-1"><span className="text-muted-foreground">Gross Profit</span><span className={`font-bold ${y.gross_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{fmtFull(y.gross_profit)}</span></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Year-over-Year Chart */}
        {yearlyData.length > 1 && (
          <Card data-testid="yearly-chart">
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4" />Year-over-Year Comparison</CardTitle></CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={yearlyData}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={v => fmt(v)} />
                    <Tooltip formatter={v => fmtFull(v)} />
                    <Legend />
                    <Bar dataKey="revenue" fill="#3b82f6" name="Revenue" />
                    <Bar dataKey="cogs" fill="#ef4444" name="COGS" />
                    <Bar dataKey="gross_profit" fill="#10b981" name="Gross Profit" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Monthly Data Table */}
        {allMonthly.length > 0 && (
          <Card data-testid="monthly-data">
            <CardHeader className="pb-2"><CardTitle className="text-base">Monthly Breakdown</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Month</TableHead><TableHead className="text-right">Revenue</TableHead><TableHead className="text-right">COGS</TableHead>
                    <TableHead className="text-right">Gross Profit</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {allMonthly.map((m, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-medium">{m.month}</TableCell>
                        <TableCell className="text-right">{fmtFull(m.revenue)}</TableCell>
                        <TableCell className="text-right text-red-600">{fmtFull(m.cogs)}</TableCell>
                        <TableCell className={`text-right font-medium ${(m.gross_profit || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{fmtFull(m.gross_profit)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Uploaded Records */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><FileSpreadsheet className="w-4 h-4" />Uploaded Statements</CardTitle></CardHeader>
          <CardContent>
            {records.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground space-y-2">
                <FileSpreadsheet className="w-12 h-12 mx-auto opacity-30" />
                <p className="text-sm">No P&L statements uploaded yet</p>
                <p className="text-xs">Upload your Zoho P&L export (PDF or Excel) to see historical financial data</p>
              </div>
            ) : (
              <div className="space-y-2">
                {records.map((r, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                    <div>
                      <p className="text-sm font-medium">{r.filename}</p>
                      <div className="flex gap-3 text-xs text-muted-foreground mt-0.5">
                        {r.year && <span>Year: {r.year}</span>}
                        {r.company_name && <span>{r.company_name}</span>}
                        <span>Uploaded: {r.created_at?.slice(0, 10)}</span>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" className="text-destructive border-destructive/30 hover:bg-destructive/10" onClick={() => setDeleteId(r.id)} data-testid={`delete-pnl-${r.id}`}>
                      <Trash2 className="w-4 h-4 mr-1" />Delete
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        <ConfirmDeleteDialog open={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={() => handleDelete(deleteId)} title="Delete P&L Record" description="This will remove this financial record." />
      </div>
    </Layout>
  );
};

export default PollenDotsFinancials;
